import React from 'react';
import { useIntl } from 'react-intl';
import { Handshake } from '@phosphor-icons/react';

import { Link } from '../../../components/Link';
import { CompanyLogo } from '../../../components/CompanyLogo';
import { CompanyFollowButton } from '../../../components/company/CompanyFollowButton';
import { TrendIndicator, type TrendDirection } from '../../../components/jc/TrendIndicator';
import { mapEmployerScoreToTier } from '../../../utils/employer-score/mapScoreToTier';
import type { AlgoliaCompanyRecord } from '../../../types/company-search-record';
import { getCombinedRemoteHybridPercentForDisplay } from '../../../utils/data/get-combined-remote-hybrid-percent-for-display';
import { buildBestCompaniesListingRatingExplainer } from '../../../utils/best-companies/build-best-companies-listing-rating-explainer';
import { isNetworkWorthy } from '../../../utils/best-companies/is-network-worthy';
import { normalizeBestCompaniesLocale } from '../../../utils/seo/best-companies-meta';
import { companyProfileUrlPath } from '../../../utils/routing/company-profile-url-path';
import { Msg, useFormatMessage } from '../../../i18n-review/client/Msg';

/** Used only by the best-companies/@dimension city/industry list rows now: the employers hub
 *  itself moved to EmployerBrandCard (see pages/best-companies/components/EmployerBrandCard.tsx),
 *  which has its own on-panel "few public postings" pill. */
function NetworkWorthyBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-bold bg-jc-gold-bg text-jc-gold"
      title="High employer score and few public postings in our index — reflects our coverage, not their actual hiring"
    >
      <Handshake className="h-3 w-3" weight="fill" />
      <Msg
        id="bestCompanies.row.networkWorthy"
        defaultMessage="Worth networking with"
        description="Badge shown on a well-scoring employer that rarely posts jobs publicly, signalling users should reach out directly instead of waiting for a listing"
      />
    </span>
  );
}

const TREND_DIRECTION: Record<string, TrendDirection> = {
  growing: 'up',
  declining: 'down',
  stable: 'flat',
};

const TREND_MESSAGES = {
  growing: { id: 'bestCompanies.row.trendGrowing', defaultMessage: 'Growing' },
  declining: { id: 'bestCompanies.row.trendDeclining', defaultMessage: 'Declining' },
  stable: { id: 'bestCompanies.row.trendStable', defaultMessage: 'Stable' },
} as const;

function trendLabel(trend: string | undefined, formatMessage: ReturnType<typeof useFormatMessage>) {
  const message = trend ? TREND_MESSAGES[trend as keyof typeof TREND_MESSAGES] : null;
  return message ? formatMessage(message) : null;
}

function useSharedRowData(company: AlgoliaCompanyRecord) {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const locale = normalizeBestCompaniesLocale(intl.locale);
  const topRolesText = company.topRoles?.slice(0, 3).join(' · ');
  const combinedRemoteHybridPct = getCombinedRemoteHybridPercentForDisplay(company);
  const dist = company.remotenessDistribution;
  const flexText =
    combinedRemoteHybridPct != null
      ? dist != null && dist.hybrid === 0 && dist.remote > 0
        ? formatMessage(
            { id: 'bestCompanies.row.remotePercent', defaultMessage: '{percent}% remote', description: "Share of a company's open roles that are fully remote, shown on the best-companies listing row" },
            { percent: combinedRemoteHybridPct },
          )
        : formatMessage(
            { id: 'bestCompanies.row.remoteHybridPercent', defaultMessage: '{percent}% remote/hybrid', description: "Share of a company's open roles that are remote or hybrid, shown on the best-companies listing row" },
            { percent: combinedRemoteHybridPct },
          )
      : null;

  const salaryRange =
    company.medianSalaryMin && company.medianSalaryMax
      ? `€${Math.round(company.medianSalaryMin / 100) * 100}–€${Math.round(company.medianSalaryMax / 100) * 100}`
      : null;

  const headcountText =
    company.companyHeadCountMin && company.companyHeadCountMax
      ? `${company.companyHeadCountMin.toLocaleString(intl.locale)}–${company.companyHeadCountMax.toLocaleString(intl.locale)}`
      : null;

  const hiringPaceText =
    typeof company.recentJobsPerMonth === 'number' && company.recentJobsPerMonth > 0
      ? formatMessage(
          { id: 'bestCompanies.row.hiringPace', defaultMessage: '~{count} new roles/mo', description: 'Recent hiring pace, average new job postings per month' },
          { count: Math.round(company.recentJobsPerMonth) },
        )
      : null;

  const ratingExplainer = buildBestCompaniesListingRatingExplainer(company, locale);

  const industryLabel =
    locale === 'fi'
      ? company.industryFi || company.industryEn
      : locale === 'sv'
        ? company.industrySv || company.industryEn
        : company.industryEn;

  return { formatMessage, locale, topRolesText, flexText, salaryRange, headcountText, hiringPaceText, ratingExplainer, industryLabel };
}

export interface BestCompaniesCompanyRowProps {
  company: AlgoliaCompanyRecord;
  rank: number;
}

/** List row for the best-companies/@dimension city/industry pages. The employers hub itself
 *  (pages/best-companies/+Page.tsx) uses EmployerBrandCard instead; the old 'podium' variant
 *  here was only ever used by the hub's top-3 podium, now removed. */
export function BestCompaniesCompanyRow({ company, rank }: BestCompaniesCompanyRowProps) {
  return <ListRow company={company} rank={rank} />;
}

function ListRow({ company, rank }: { company: AlgoliaCompanyRecord; rank: number }) {
  const { formatMessage, locale, topRolesText, flexText, salaryRange, hiringPaceText, ratingExplainer, industryLabel } = useSharedRowData(company);
  const score = company.employerScore;
  const trendDirection = company.hiringTrend ? TREND_DIRECTION[company.hiringTrend] : undefined;
  const label = trendLabel(company.hiringTrend, formatMessage);
  const networkWorthy = isNetworkWorthy(company);

  return (
    <Link
      href={companyProfileUrlPath(locale, company.objectID, company.profileSlug)}
      locale={locale as 'en' | 'fi' | 'sv'}
      className="flex items-start gap-2.5 rounded-xl border border-jc-border bg-jc-surface p-3 shadow-md transition-all hover:border-jc-accent-soft hover:bg-jc-elevated/40 group sm:gap-4 sm:rounded-2xl sm:p-4"
    >
      <div className="shrink-0">
        <CompanyLogo
          companyName={company.objectID}
          size={48}
          alt={company.companyName ?? ''}
          variant="default"
          isTyomarkkinatori={false}
          loading={rank <= 5 ? 'eager' : 'lazy'}
        />
      </div>

      <div className="flex-1 min-w-0">
        <div className="mb-0.5 flex items-start justify-between gap-1.5 sm:mb-1 sm:gap-2">
          <h3 className="truncate font-display text-sm font-bold text-jc-ink transition-colors group-hover:text-jc-accent">
            {company.companyName}
          </h3>
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
            {trendDirection && label ? <TrendIndicator direction={trendDirection}>{label}</TrendIndicator> : null}
            {score != null ? (
              <span
                className="inline-flex h-6 min-w-[2.25rem] items-center justify-center rounded-full bg-jc-accent-muted px-2 font-display text-[11px] font-bold text-jc-accent"
                title={`Employer Score: ${mapEmployerScoreToTier(score)}`}
              >
                {mapEmployerScoreToTier(score)}
              </span>
            ) : null}
            <CompanyFollowButton
              compact
              surface="best-companies"
              companyId={company.objectID}
              companyName={company.companyName}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            />
          </div>
        </div>
        <p className="mb-1 text-xs text-jc-muted sm:mb-1.5">
          {[industryLabel, company.registryCity].filter(Boolean).join(' · ')}
        </p>
        {networkWorthy ? (
          <div className="mb-1 sm:mb-1.5">
            <NetworkWorthyBadge />
          </div>
        ) : null}
        <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] text-jc-muted sm:gap-x-3 sm:text-xs">
          {flexText && <span className="text-jc-accent font-medium">{flexText}</span>}
          {salaryRange && (
            <span>
              <Msg id="bestCompanies.row.medianPrefix" defaultMessage="Median" />{' '}
              <Msg id="bestCompanies.row.perMonth" defaultMessage="{range}/mo" description="Median salary range suffixed with a per-month marker, shown on the best-companies listing row" values={{ range: salaryRange }} />
            </span>
          )}
          {hiringPaceText && <span>{hiringPaceText}</span>}
          {topRolesText && <span className="text-jc-contrast-subtle">{topRolesText}</span>}
        </div>
        <p className="mt-1 line-clamp-3 text-[11px] leading-snug text-jc-muted sm:mt-1.5 sm:text-xs sm:leading-relaxed">
          {ratingExplainer}
        </p>
      </div>
    </Link>
  );
}

